import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { injectIntl, FormattedMessage } from 'react-intl';
import messages from '../../../locale/messages';
import history from '../../../core/history';
import cx from 'classnames';
import MarkdownIt from 'markdown-it';
import { graphql, compose } from 'react-apollo';

import FindingReview from './FindingReview';
import findingReviewQuery from './findingReviewQuery.graphql';
import reviewFindingMutation from './reviewFindingMutation.graphql';

// Style
import cs from '../../../components/commonStyle.css';
import s from './CarScanAiDocumentManagement.css';
const md = new MarkdownIt();

class InspectionReport extends React.Component {

  static propTypes = {
    reservationData: PropTypes.object.isRequired,
    reportData: PropTypes.object.isRequired,
    intl: PropTypes.object.isRequired,
    onBack: PropTypes.func.isRequired,
    // Supplied by the graphql HOCs below.
    findingReviews: PropTypes.object,
    reviewFinding: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      currentImageIndex: 0,
      decidingId: null,
      reviewError: null,
    };
  }

  // The review rows for this reservation's findings, keyed by position in the
  // report. Held apart from the report itself because the report is the
  // analysis service's payload and these are MotoStream's decisions about it —
  // two different things with two different lifetimes.
  //
  // A query failure leaves this empty and the report still renders. An operator
  // who cannot see the review controls is better off than one who cannot see
  // the report.
  reviews() {
    const { findingReviews } = this.props;
    const result = findingReviews && findingReviews.getFindingReviewQueue;
    return (result && result.status === 200 && result.results) || [];
  }

  reviewFor = (index) => this.reviews().find((r) => r.findingIndex === index) || null;

  handleDecision = async (reviewId, state, note) => {
    this.setState({ decidingId: reviewId, reviewError: null });

    try {
      const { data } = await this.props.reviewFinding({
        variables: { reviewId, state, note: note || null },
      });

      const result = data && data.reviewFinding;
      if (result && result.status === 200) {
        // Re-read rather than patching the cache by hand. Another operator may
        // have decided something in the meantime, and the server's answer is
        // the one that counts.
        await this.props.findingReviews.refetch();
      } else {
        this.setState({ reviewError: (result && result.errorMessage) || 'Could not record that decision.' });
      }
    } catch (e) {
      this.setState({ reviewError: 'Could not record that decision.' });
    } finally {
      this.setState({ decidingId: null });
    }
  };

  handleBack = () => {
    this.props.onBack();
  };

  handleMarkReportDone = () => {
    // Handle marking report as done
    console.log('Marking report as done');
    // You can add API call here if needed
  };

  handleTakeAnotherScan = () => {
    // Handle taking another scan
    console.log('Taking another scan');
    // You can add navigation logic here if needed
  };

  // Helper method to get severity CSS class
  getSeverityClass = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'severe': return 'severe';
      case 'moderate': return 'moderate';
      case 'minor': return 'minor';
      default: return 'minor';
    }
  };

  // Helper method to calculate fuel gauge angle
  getFuelAngle = (fuelLevel) => {
    // Convert fuel level to angle (0 = empty, 180 = full)
    const level = parseFloat(fuelLevel) || 0;
    return Math.min(180, Math.max(0, (level / 100) * 180));
  };

  // Helper method to clean text
  cleanText = (text) => {
    if (!text) return '';

    const html = md.render(text);
    return { __html: html };

  };

  render() {
    const { reservationData, reportData, intl } = this.props;

    if (!reservationData || !reportData) {
      return (
        <div className={cx(s.pagecontentWrapper)}>
          <div className="card mb-4">
            {/* Added s['card-body'] here */}
            <div className={cx("card-body", s['card-body'])} p-3>
              <p>No report data available</p>
            </div>
          </div>
        </div>
      );
    }

    // Get part reports for AI inspections
    const partReports = reportData.part_reports || [];
    const overallSummary = reportData.overall_summary || '';

    return (
      <div className={cx(s.pagecontentWrapper)}>
        {/* Header */}
        <div className="card mb-4 border-0 shadow-sm">
          {/* Added s['card-body'] here */}
          <div className={cx("card-body", s['card-body'])} p-4>
            {/* Title and Back Button in a single line */}
            <div className={cx(cs.displayFlex, cs.alignCenter, cs.spaceBetween, cs.spaceBottom6)}>
              <h1 className={cx(cs.fontWeightBold, s.pageTitle)}>
                Inspection Report
              </h1>
              <button
                type="button"
                className={cx(cs.btnSecondary, 'px-4 py-2')}
                onClick={this.handleBack}
                style={{ borderRadius: '8px', fontWeight: '500' }}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back
              </button>
            </div>

            {/* Overall Summary Section with improved styling */}
            {overallSummary && (
              <div className={cx(s.inspectionReportCard)}>
                <div className={cx(s.cardHeader)}>
                  <h2 className="mb-0 fw-bold">
                    <i className="fas fa-clipboard-list me-2"></i>
                    Overall Summary
                  </h2>
                </div>
                {/* Added s['card-body'] here */}
                <div className={cx(s.cardBody)}>
                  <div className="mb-0 fs-5 p-3">
                    <i className="fas fa-info-circle me-2"></i>
                    {overallSummary}
                  </div>
                </div>
              </div>
            )}

            {/* Images Inspection Section */}
            <div className={cx(cs.spaceBottom4, s.cardTitle)}>
              <h2 className="mb-0 fw-bold">
                <i className="fas fa-images me-2"></i>
                Images Inspection
              </h2>
            </div>
            {/* alert alert-warning means the same thing in Bootstrap 3, 4 and
                5, so this renders whichever the admin screen actually gets. */}
            {this.state.reviewError && (
              <div className="alert alert-warning" role="alert">{this.state.reviewError}</div>
            )}
            <div className={cx("card mb-4 inspectionReportCard border-0 shadow-sm")}>
              {/* Added s['card-body'] here */}
              <div className={cx("card-body", s['card-body'])}>
                {partReports.length > 0 ? (
                  <div className="">
                    {partReports.map((part, index) => (
                      <div key={index} className={cx(s.inspectionReportCard, cs.spaceBottom4, s.paddingCard)}>
                        <div className={cx("row g-4", s.row)}>
                          {part.before_photo_url && (
                            <div className={cx("col-md-6", s['col-md-6'])}>
                              <div className={cx(s.imageType, cs.spaceBottom5)}>
                                <span className="partTitle bg-info me-2 px-3 py-2">
                                  <i className="fas fa-cog me-1"></i>
                                  Part: {part.part_name}
                                </span>
                              </div>
                              <div className={cx("text-center p-4 border rounded-3 shadow-sm", s.imageContainer, s.marginBottom)}>
                                <img
                                  src={part.before_photo_url}
                                  alt={`Before ${part.part_name}`}
                                  className={cx("img-fluid rounded-3 shadow-sm mb-3", s.imgFluid)}
                                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '250px', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                                  }}
                                />
                                <div className={cx(cs.spaceTop2)}>
                                  <span className={cx("badge bg-secondary px-3 py-2", s.badge)}>
                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                    Before
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                          {part.after_photo_url && (
                            <div className={cx("col-md-6", s['col-md-6'])}>
                              <div className={cx(s.imageType, cs.spaceBottom5)}>
                                <span className={`partTitle ${part.severity === 'Severe' ? 'bg-danger' : part.severity === 'Moderate' ? 'bg-warning' : 'bg-success'} px-3 py-2`}>
                                  <i className={`fas ${part.severity === 'Severe' ? 'fa-exclamation-triangle' : part.severity === 'Moderate' ? 'fa-exclamation-circle' : 'fa-check-circle'} me-1`}></i>
                                  {part.severity} Damage
                                </span>
                              </div>

                              <div className={cx("text-center p-4 border rounded-3 shadow-sm", s.imageContainer, s.marginBottom)}>
                                <img
                                  src={part.after_photo_url}
                                  alt={`After ${part.part_name}`}
                                  className={cx("img-fluid rounded-3 shadow-sm mb-3", s.imgFluid)}
                                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '250px', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                                  }}
                                />
                                <div className={cx(cs.spaceTop2)}>
                                  <span className={cx("badge bg-success px-3 py-2", s.badge)}>
                                    <i className="fas fa-check-circle me-1"></i>
                                    After
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Damage Analysis and Recommendations in a row */}

                        {part.damage_analysis && (
                          <div className="row mt-4">
                            <div className="col-md-12 mb-3">
                              <div className="card h-100 border-0 shadow-sm">
                                <div className={cx("card-body", s['card-body'])}>
                                  <div dangerouslySetInnerHTML={this.cleanText(part.damage_analysis)} />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Keyed by position, not by part name: a report can
                            carry two entries for the same part, and matching on
                            the name would show one finding's decision on both. */}
                        <FindingReview
                          review={this.reviewFor(index)}
                          confidence={part.confidence}
                          busy={this.state.decidingId === (this.reviewFor(index) || {}).id}
                          onDecide={this.handleDecision}
                        />



                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i className="fas fa-image text-muted" style={{ fontSize: '4rem' }}></i>
                    <p className="text-muted mt-3 fs-5">No images available for inspection</p>
                  </div>
                )}
              </div>
            </div>

            {/* Odometer Inspection Section */}
            {reportData.odometer_before && reportData.odometer_after && (
              <div className="card mb-4 inspectionReportCard border-0 shadow-sm">
                <div className="card-header bg-info text-white py-3">
                  <h6 className="mb-0 fw-bold">
                    <i className="fas fa-tachometer-alt me-2"></i>
                    Odometer Inspection
                  </h6>
                </div>
                {/* Added s['card-body'] here */}
                <div className={cx("card-body", s['card-body'])}>
                  <div className="row text-center">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="text-muted mb-2">Before Test</div>
                        <div className="border rounded-circle d-inline-flex align-items-center justify-content-center"
                          style={{ width: '100px', height: '100px', border: '2px solid #007bff' }}>
                          <span className="text-primary fw-bold fs-4">{reportData.odometer_before}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="text-muted mb-2">After Test</div>
                        <div className="border rounded-circle d-inline-flex align-items-center justify-content-center"
                          style={{ width: '100px', height: '100px', border: '2px solid #007bff' }}>
                          <span className="text-primary fw-bold fs-4">{reportData.odometer_after}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Fuel Report Section */}
            {reportData.fuel_before && reportData.fuel_after && (
              <div className="card mb-4 inspectionReportCard border-0 shadow-sm">
                <div className="card-header bg-warning text-dark py-3">
                  <h6 className="mb-0 fw-bold">
                    <i className="fas fa-gas-pump me-2"></i>
                    Fuel Report
                  </h6>
                </div>
                {/* Added s['card-body'] here */}
                <div className={cx("card-body", s['card-body'])}>
                  <div className="row text-center">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="text-muted mb-2">Before Scan</div>
                        <div className="position-relative d-inline-block">
                          <div className="border rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '100px', height: '100px', border: '2px solid #28a745' }}>
                            <div className="position-absolute" style={{
                              width: '4px',
                              height: '40px',
                              background: '#28a745',
                              top: '50%',
                              left: '50%',
                              transformOrigin: 'bottom center',
                              transform: `translate(-50%, -50%) rotate(${this.getFuelAngle(reportData.fuel_before)}deg)`
                            }}></div>
                            <span className="position-absolute text-success fw-bold" style={{ bottom: '5px', left: '50%', transform: 'translateX(-50%)' }}>E</span>
                            <span className="position-absolute text-success fw-bold" style={{ top: '5px', left: '50%', transform: 'translateX(-50%)' }}>F</span>
                            <span className="position-absolute text-success fw-bold mt-2" style={{ fontSize: '0.8rem' }}>
                              {reportData.fuel_before}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="text-muted mb-2">After Scan</div>
                        <div className="position-relative d-inline-block">
                          <div className="border rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '100px', height: '100px', border: '2px solid #28a745' }}>
                            <div className="position-absolute" style={{
                              width: '4px',
                              height: '40px',
                              background: '#28a745',
                              top: '50%',
                              left: '50%',
                              transformOrigin: 'bottom center',
                              transform: `translate(-50%, -50%) rotate(${this.getFuelAngle(reportData.fuel_after)}deg)`
                            }}></div>
                            <span className="position-absolute text-success fw-bold" style={{ bottom: '5px', left: '50%', transform: 'translateX(-50%)' }}>E</span>
                            <span className="position-absolute text-success fw-bold" style={{ top: '5px', left: '50%', transform: 'translateX(-50%)' }}>F</span>
                            <span className="position-absolute text-success fw-bold mt-2" style={{ fontSize: '0.8rem' }}>
                              {reportData.fuel_after}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}


          </div>
        </div>
      </div>
    );
  }
}

export default compose(
  injectIntl,
  withStyles(s, cs),
  graphql(findingReviewQuery, {
    name: 'findingReviews',
    options: (props) => ({
      variables: { reservationId: props.reservationData && props.reservationData.id },
      // network-only because an operator can be looking at a report another
      // operator is deciding on. A cached queue would show findings as needing
      // review after somebody else had already dealt with them.
      fetchPolicy: 'network-only',
    }),
  }),
  graphql(reviewFindingMutation, { name: 'reviewFinding' }),
)(InspectionReport);